import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const WhatIsCuripodPremadeCurriculumAlignedWritingPractice = () => {
  const post = getBlogPost('what-is-curipod-premade-curriculum-aligned-writing-practice');
  return (
    <BlogArticle post={post}>
      <p>
        {
          'Curipod, previously known for interactive AI lessons, is now a writing practice platform for grades 3–12. In Curipod, you now find premade, quality-assured writing practice aligned to your curriculum, built on authentic texts by real, published authors. Students read those texts, write about what they read, get feedback, revise and discuss together, with the teacher leading the room.'
        }
      </p>
      <p>
        {
          'What has changed is our focus. Everything we build now serves one goal: helping students become stronger readers and writers. We call it '
        }
        <strong>{'Writing Practice Students Love'}</strong>
        {'.'}
      </p>
      <h2>{'What you find in Curipod today'}</h2>
      <p>
        {
          "Curipod is teacher-led writing practice aligned to your district's curriculum. Students read, write and discuss. Not alone on a screen. Together."
        }
      </p>
      <p>{'When you open Curipod, the practice is already there:'}</p>
      <ul>
        <li>
          <strong>{'Premade.'}</strong>
          {
            ' You find writing practice already built and aligned to your curriculum and scope and sequence. Open the week, pick the practice for your unit and teach it. There is no extra prep.'
          }
        </li>
        <li>
          <strong>{'Quality assured.'}</strong>
          {
            ' Every premade practice in the library is quality assured, so you can teach it with confidence from the first day.'
          }
        </li>
        <li>
          <strong>{'Curriculum aligned.'}</strong>
          {
            ' Curipod is built on the curriculum you already teach, and we are aligned with more than 55 top-rated curricula. Practice follows the skills and units in your scope and sequence.'
          }
        </li>
        <li>
          <strong>{'Authentic texts by real authors.'}</strong>
          {
            ' Students read real writing by real, published authors, and then write about it.'
          }
        </li>
      </ul>
      <p>{'Each practice follows a simple cycle:'}</p>
      <ol>
        <li>
          <strong>{'Read.'}</strong>
          {
            ' Students start with an authentic text connected to the skills your class is working on.'
          }
        </li>
        <li>
          <strong>{'Write.'}</strong>
          {
            ' They respond to a prompt about that text, from a short answer to an essay or extended constructed response.'
          }
        </li>
        <li>
          <strong>{'Get feedback.'}</strong>
          {
            ' Each student gets specific, actionable feedback the moment they submit: a Glow (what is working) and a Grow (one thing to improve).'
          }
        </li>
        <li>
          <strong>{'Discuss.'}</strong>
          {
            ' The teacher shows student exemplars and leads a class discussion about what makes a strong response.'
          }
        </li>
        <li>
          <strong>{'Reflect and revise.'}</strong>
          {
            ' Students read their feedback, go back to the text, and revise and improve their writing.'
          }
        </li>
      </ol>
      <p>
        {
          "All of this happens in one class period. The feedback approach is based on John Hattie's research, which found that feedback is one of the most powerful influences on learning. In Curipod it's specific, timely and actionable. And students get a chance to talk about their writing, and revise there and then."
        }
      </p>
      <h2>{'Why we chose writing'}</h2>
      <p>
        {
          'We spend as much time as we can in classrooms with the teachers and leaders who use Curipod. Over the past year, the same themes kept coming up: students were excited to write, the feedback helped them revise, and writing was where teachers wanted more support.'
        }
      </p>
      <p>{'An instructional coach at an elementary school put the reason simply:'}</p>
      <blockquote>
        <p>
          {
            '"Research shows that when students improve in writing, their reading and other literacy components also improve."'
          }
        </p>
      </blockquote>
      <p>
        {
          'Teachers also told us about time. A class of thirty students can write thirty responses in ten minutes. Reading each one carefully and giving useful feedback can take an entire evening. That is a limit of time that every teacher faces. A fifth grade teacher in one of our published case studies described it this way:'
        }
      </p>
      <blockquote>
        <p>
          {
            '"I cannot give immediate feedback to 30 students at once, but with Curipod, it is possible."'
          }
        </p>
      </blockquote>
      <p>
        {
          'Hearing this again and again convinced us. We put all our effort into writing practice, so we could go deeper on what matters most: practice that matches your curriculum, texts worth reading, clear feedback and simple revision.'
        }
      </p>
      <h2>{'Reading and writing, together'}</h2>
      <p>
        {
          'Reading and writing are closely connected, and the research on how students learn to read and write points in the same direction.'
        }
      </p>
      <p>
        {
          'Literacy researcher Timothy Shanahan describes three connections. Reading and writing draw on the same knowledge and skills. Both are ways of communicating, so writers think about their readers and readers think about authors. And the two can be used together, for example when students write about a text to understand it better.'
        }
      </p>
      <p>
        {'The evidence supports teaching them side by side. In '}
        <em>{'Writing to Read'}</em>
        {
          ', Steve Graham and Michael Hebert reviewed studies of students in grades 2–12. They found that reading comprehension improves when students write about the texts they read, when they are taught the skills that go into writing, and when they simply write more. Writing about reading had a positive effect in 57 of 61 outcomes, across English, science and social studies. A later meta-analysis by Graham and colleagues found that literacy programs that balance reading and writing instruction strengthened both reading and writing.'
        }
      </p>
      <p>
        {
          'Teachers see this in their classrooms too. A fourth grade reading and writing teacher told us:'
        }
      </p>
      <blockquote>
        <p>
          {
            '"I think what writing really, really is testing is comprehension of reading and being able to take that what you learned and put it into words, word form in a cohesive manner so somebody else can understand it."'
          }
        </p>
      </blockquote>
      <p>
        {
          "This is why every Curipod practice starts with an authentic text. When students write about real writing by real authors, they practice what the research describes: reading closely, thinking about the author's choices and explaining their understanding in their own words. Their feedback then sends them back to the text to explain it more clearly."
        }
      </p>
      <h2>{'What stays the same'}</h2>
      <p>
        {
          'Some things about Curipod will not change, because they are the reasons teachers chose us in the first place.'
        }
      </p>
      <p>
        <strong>{'Teachers lead.'}</strong>
        {
          ' Curipod is built around whole-class lessons with you as the facilitator. You choose the practice, run the room and decide when students revise or discuss. Curipod is 100% teacher controlled.'
        }
      </p>
      <p>
        <strong>{'Students learn together.'}</strong>
        {
          " Students share their writing, discuss feedback and learn from each other's responses."
        }
      </p>
      <p>
        <strong>{'Privacy comes first.'}</strong>
        {
          ' Students do not need accounts, there are no chatbots, and Curipod is FERPA, COPPA and GDPR compliant. Curipod does not use student data to train its models.'
        }
      </p>
      <p>
        <strong>{'It is free to start.'}</strong>
        {
          ' Any teacher can sign up for free and use weekly writing practice with Glow and Grow feedback.'
        }
      </p>
      <h2>{'What it means for teachers'}</h2>
      <p>
        {
          'For a classroom teacher, premade practice means less searching and less planning. You can find practice that matches the unit you are teaching this week, with the text, prompt and feedback ready to go, and it fits into the block you already teach.'
        }
      </p>
      <p>
        {
          'It also means a routine students can learn. Read, write, get feedback, discuss, revise. Once students know the cycle, a short practice fits into a mini-lesson, a small group or the end of a reading block. As a fourth grade ELA and social studies teacher told us, "I think writing is so important with reading."'
        }
      </p>
      <p>
        <Link href="/signup" passHref>
          <a>{'Sign up for free and try a writing practice with your class'}</a>
        </Link>
        {'.'}
      </p>
      <h2>{'What it means for ELA directors and literacy leaders'}</h2>
      <p>
        {
          'If you lead literacy for a campus or a district, you already have a curriculum and a scope and sequence. Premade, quality-assured practice aligned to that curriculum helps the writing your program calls for actually happen in every classroom, right alongside the reading.'
        }
      </p>
      <p>
        {
          'When every campus uses the same aligned practice and the same authentic texts, every teacher can give students regular chances to write about what they read, including newer teachers. It puts strong instruction within reach of every teacher.'
        }
      </p>
      <p>
        {
          'You also get a clearer view of growth. You can track writing growth and see which skills need attention across every classroom and school. Feedback is aligned to state rubrics, so the skills students practice match the skills they are asked to show. Curipod scores are practice scores and are separate from official test scores. They show growth from attempt one to attempt two and across the year.'
        }
      </p>
      <p>
        {
          'In our published case studies, students in Curipod classrooms improved by up to 23 percentage points on state reading and writing assessments, with the students who were furthest behind making the largest gains.'
        }
      </p>
      <h2>{'Three places to start this year'}</h2>
      <ol>
        <li>
          <strong>
            {'Start with the premade practice for a unit you already teach.'}
          </strong>
          {
            ' Pick one upcoming unit and find the Curipod writing practice aligned to it. Ask teachers to use it once a week during that unit, so writing grows out of the reading students are already doing.'
          }
        </li>
        <li>
          <strong>{'Make attempt two the expectation.'}</strong>
          {
            ' Revision is where much of the growth happens. Ask teachers to leave a few minutes after feedback so every student reads their Glow and Grow, looks back at the text and revises.'
          }
        </li>
        <li>
          <strong>{'Review growth by skill with your principals.'}</strong>
          {
            ' Once a grading period, sit down with principals or instructional coaches and look at writing growth by skill across campuses. Choose one skill to focus on for the next few weeks, and let teachers pick the practice that fits their students.'
          }
        </li>
      </ol>
      <h2>{'Writing Practice Students Love'}</h2>
      <p>
        {
          'Curipod has always been about students learning together, with a teacher leading the room. Today that energy goes into reading and writing, with premade, quality-assured practice aligned to your curriculum and built on authentic texts by real authors. Teachers get time back and a clear picture of growth.'
        }
      </p>
      <p>
        {
          'If you want to see what Writing Practice Students Love looks like in your classrooms, '
        }
        <Link href="/signup" passHref>
          <a>{'sign up for Curipod for free'}</a>
        </Link>
        {' and explore writing practice aligned to your curriculum.'}
      </p>
      <h2>{'References'}</h2>
      <ul>
        <li>
          {'Curipod. Homepage: Writing Practice Students Love. '}
          <Link href="/" passHref>
            <a>{'https://curipod.com'}</a>
          </Link>
        </li>
        <li>
          {'Curipod. Pricing and plans. '}
          <Link href="/pricing" passHref>
            <a>{'https://curipod.com/pricing'}</a>
          </Link>
        </li>
        <li>
          {'Curipod. Case Studies & Research. '}
          <Link href="/case-studies" passHref>
            <a>{'https://curipod.com/case-studies'}</a>
          </Link>
        </li>
        <li>
          {'Curipod. Case study: Transforming Writing Instruction in Southern CA. '}
          <Link href="/case-studies/curipod-case-study-southern-ca" passHref>
            <a>{'https://curipod.com/case-studies/curipod-case-study-southern-ca'}</a>
          </Link>
        </li>
        <li>
          {'Curipod. About us. '}
          <Link href="/about-us" passHref>
            <a>{'https://curipod.com/about-us'}</a>
          </Link>
        </li>
        <li>
          {'Graham, S., & Hebert, M. (2010). '}
          <em>{'Writing to Read: Evidence for How Writing Can Improve Reading'}</em>
          {'. Carnegie Corporation of New York / Alliance for Excellent Education. '}
          <a
            href="https://www.carnegie.org/publications/writing-to-read-evidence-for-how-writing-can-improve-reading/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://www.carnegie.org/publications/writing-to-read-evidence-for-how-writing-can-improve-reading/'
            }
          </a>
        </li>
        <li>
          {
            'Graham, S., Liu, X., Aitken, A., Ng, C., Bartlett, B., Harris, K. R., & Holzapfel, J. (2018). Effectiveness of literacy programs balancing reading and writing instruction: A meta-analysis. '
          }
          <em>{'Reading Research Quarterly, 53'}</em>
          {'(3), 279–304. '}
          <a
            href="https://www.readingrockets.org/resources/resource-library/effectiveness-literacy-programs-balancing-reading-and-writing"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://www.readingrockets.org/resources/resource-library/effectiveness-literacy-programs-balancing-reading-and-writing'
            }
          </a>
        </li>
        <li>
          {
            'Shanahan, T. How can we take advantage of reading-writing relationships? Shanahan on Literacy. '
          }
          <a
            href="https://www.shanahanonliteracy.com/blog/how-can-we-take-advantage-of-reading-writing-relationships"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://www.shanahanonliteracy.com/blog/how-can-we-take-advantage-of-reading-writing-relationships'
            }
          </a>
        </li>
        <li>
          {'Hattie, J., & Timperley, H. (2007). The power of feedback. '}
          <em>{'Review of Educational Research, 77'}</em>
          {'(1), 81–112. '}
          <a
            href="https://eric.ed.gov/?id=EJ782448"
            target="_blank"
            rel="noopener noreferrer"
          >
            {'https://eric.ed.gov/?id=EJ782448'}
          </a>
        </li>
      </ul>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default WhatIsCuripodPremadeCurriculumAlignedWritingPractice;
